import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Wand2, Loader2, Undo2 } from 'lucide-react'
import { supabase } from '../lib/supabaseClient'
import { cn } from '../lib/utils'

// Inline bilingual writing assistant. Drop next to any composer:
//   <RewriteButton value={draft} onRewritten={setDraft} />
// Tap the wand → style chips appear → pick one → the rewritten text
// replaces the draft (with a one-tap undo back to the original).

const STYLES = ['improve', 'formal', 'casual', 'to_ar', 'to_en']

export default function RewriteButton({ value, onRewritten, className }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(null) // style key while running
  const [original, setOriginal] = useState(null)

  const disabled = !value || !value.trim()

  const run = async (style) => {
    if (busy || disabled) return
    setBusy(style)
    const { data, error } = await supabase.functions.invoke('rewrite-text', {
      body: { text: value, style },
    })
    setBusy(null)
    if (error || !data?.text) return
    setOriginal(value)
    onRewritten(data.text)
    setOpen(false)
  }

  const undo = () => {
    if (original == null) return
    onRewritten(original)
    setOriginal(null)
  }

  return (
    <div className={cn('flex items-center gap-1', className)}>
      {original != null && (
        <button
          type="button"
          onClick={undo}
          aria-label={t('rewrite.undo')}
          title={t('rewrite.undo')}
          className="p-1.5 rounded-md hover:bg-accent text-muted-foreground"
        >
          <Undo2 className="w-4 h-4" />
        </button>
      )}
      {open && !busy && (
        <div className="flex flex-wrap gap-1">
          {STYLES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => run(s)}
              className="px-2 py-1 rounded-md bg-app-primary-500/10 text-app-primary-500 text-[11px] font-semibold"
              dir="auto"
            >
              {t(`rewrite.style.${s}`)}
            </button>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-label={t('rewrite.title')}
        title={t('rewrite.title')}
        className={cn(
          'p-1.5 rounded-md transition-colors',
          open ? 'bg-app-primary-500/15 text-app-primary-500' : 'hover:bg-accent text-muted-foreground',
          disabled && 'opacity-40',
        )}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin text-app-primary-500" /> : <Wand2 className="w-4 h-4" />}
      </button>
    </div>
  )
}
